import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import JobCard from "@/components/jobs/JobCard";
import Reveal from "@/components/ui/reveal";
import { useJobs } from "@/hooks/useJobs";
import { useAuth } from "@/hooks/useAuth";
import {
  Briefcase,
  Users,
  Shield,
  Search,
  ArrowRight,
  Check,
  FileText,
  Send,
  Building2,
  UserPlus,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const studentSteps = [
  { icon: Search, title: "Consulte les offres", desc: "Filtre par lieu, type de contrat et rémunération." },
  { icon: UserPlus, title: "Trouve une opportunité adaptée", desc: "Des jobs pensés pour ton emploi du temps." },
  { icon: Send, title: "Candidate simplement", desc: "Postule en quelques clics avec ton CV." },
];

const employerSteps = [
  { icon: FileText, title: "Publiez votre offre", desc: "En quelques minutes, mettez en avant votre annonce." },
  { icon: Users, title: "Touchez des étudiants motivés", desc: "Visibilité ciblée sur des jeunes de 16 à 25 ans." },
  { icon: Send, title: "Recevez des candidatures", desc: "Consultez les profils et CV directement en ligne." },
];

const features = [
  {
    icon: Briefcase,
    title: "Jobs flexibles",
    desc: "Des offres adaptées à ton emploi du temps étudiant : week-ends, vacances, mi-temps.",
  },
  {
    icon: Users,
    title: "Proximité locale",
    desc: "Trouve des opportunités près de chez toi : restaurants, commerces, PME de ta région.",
  },
  {
    icon: Shield,
    title: "100% RGPD",
    desc: "Tes données sont protégées et hébergées en Europe. Pas de cookies non essentiels.",
  },
];

const Index = () => {
  const { user, role, loading: authLoading } = useAuth();
  const { data: jobs, isLoading } = useJobs();
  const featuredJobs = jobs?.slice(0, 3) || [];

  // Redirect logged-in users without a role to role selection (OAuth first login)
  if (!authLoading && user && !role) {
    return <Navigate to="/choix-role" replace />;
  }

  // Redirect logged-in students to job offers page
  if (!authLoading && user && role === "student") {
    return <Navigate to="/offres" replace />;
  }

  return (
    <Layout>
      <Seo
        title="JobTeen — Jobs étudiants & stages en Europe"
        description="Trouvez un job étudiant, un job d'été ou un stage près de chez vous. Plateforme gratuite, conforme RGPD, dédiée aux 16-25 ans."
        path="/"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" aria-hidden="true" />
        <div className="container relative pt-14 pb-16 md:pt-24 md:pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3.5 py-1.5 text-[13px] font-medium text-muted-foreground shadow-xs">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                </span>
                {jobs?.length || 0} offres disponibles
              </div>

              <h1 className="type-display mt-7">
                Trouve un job étudiant, un stage ou un job d'été
                <span className="text-muted-foreground"> près de chez toi.</span>
              </h1>

              <p className="type-lead mt-6 measure-tight">
                JobTeen connecte les étudiants aux entreprises locales — simplement, rapidement, et gratuitement pour
                les candidats.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="w-full sm:w-auto">
                  <Link to="/offres">
                    <Search className="h-5 w-5" />
                    Voir les offres
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
                  <Link to="/inscription?type=recruteur">
                    Publier une offre
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>

              <ul className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-muted-foreground">
                {["100% gratuit", "Données hébergées en Europe", "Simple et rapide"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-success shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative hidden lg:block animate-fade-in">
              <img
                src="/images/hero-students.jpg"
                alt="Étudiants travaillant ensemble"
                className="relative rounded-2xl shadow-lg w-full h-auto"
                width={612}
                height={344}
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section surface-alt border-y border-border/60">
        <div className="container">
          <Reveal className="max-w-2xl">
            <p className="type-eyebrow">Pourquoi JobTeen</p>
            <h2 className="type-h2 mt-4">Une plateforme pensée pour les jeunes et les recruteurs locaux.</h2>
          </Reveal>
          <div className="mt-12 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {features.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 90}>
                <Card className="h-full bg-card shadow-xs transition-all duration-300 ease-soft hover:-translate-y-[3px] hover:shadow-md">
                  <CardContent className="p-7 md:p-8">
                    <div className="w-11 h-11 rounded-md bg-primary/10 flex items-center justify-center">
                      <feature.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="type-h3 mt-6 text-lg sm:text-xl">{feature.title}</h3>
                    <p className="mt-3 text-sm md:text-[15px] text-muted-foreground leading-relaxed">{feature.desc}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured jobs */}
      <section className="section">
        <div className="container">
          <Reveal className="flex flex-col md:flex-row justify-between items-start md:items-end gap-5">
            <div className="max-w-xl">
              <p className="type-eyebrow">Offres à la une</p>
              <h2 className="type-h2 mt-4">Les dernières opportunités publiées.</h2>
            </div>
            <Button asChild variant="outline">
              <Link to="/offres">
                Voir toutes les offres
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </Reveal>

          <div className="mt-12 md:mt-16">
            {isLoading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-72 rounded-lg" />
                ))}
              </div>
            ) : featuredJobs.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                {featuredJobs.map((job, i) => (
                  <Reveal key={job.id} delay={i * 90} className="h-full">
                    <JobCard
                      job={{
                        id: job.id,
                        title: job.title,
                        company: job.company_name || "Entreprise",
                        location: job.location,
                        type: job.job_type as "job-etudiant" | "job-ete" | "stage",
                        schedule: job.schedule || "",
                        postedAt: new Date(job.created_at).toLocaleDateString("fr-FR"),
                        description: job.description || "",
                      }}
                    />
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 rounded-2xl border border-dashed border-border bg-muted/30">
                <p className="font-medium text-foreground">Toutes les offres disponibles apparaîtront ici.</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Reviens bientôt — de nouvelles entreprises rejoignent JobTeen chaque semaine.
                </p>
                <Button asChild className="mt-7">
                  <Link to="/inscription?type=recruteur">Publier une offre</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section surface-alt border-y border-border/60">
        <div className="container">
          <Reveal className="max-w-2xl">
            <p className="type-eyebrow">Comment ça marche</p>
            <h2 className="type-h2 mt-4">Un parcours simple, que tu sois étudiant ou recruteur.</h2>
          </Reveal>

          <div className="mt-12 md:mt-16 grid md:grid-cols-2 gap-5 lg:gap-6">
            {[
              { label: "Étudiants", icon: Users, steps: studentSteps },
              { label: "Entreprises", icon: Building2, steps: employerSteps },
            ].map((group, gi) => (
              <Reveal key={group.label} delay={gi * 110}>
                <Card className="h-full">
                  <CardContent className="p-7 md:p-9">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                        <group.icon className="h-5 w-5" />
                      </div>
                      <h3 className="type-h3 text-lg sm:text-xl">{group.label}</h3>
                    </div>
                    <ol className="mt-7 space-y-6">
                      {group.steps.map((step, i) => (
                        <li key={step.title} className="flex gap-4">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border/70 bg-background text-[13px] font-semibold text-muted-foreground">
                            {i + 1}
                          </span>
                          <div>
                            <p className="font-semibold tracking-[-0.01em]">{step.title}</p>
                            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="rounded-2xl bg-foreground text-background px-7 py-14 md:px-16 md:py-20 text-center">
              <h2 className="type-h2 text-background">Prêt à trouver ton job ?</h2>
              <p className="mt-5 mx-auto max-w-xl text-base md:text-lg text-background/70 leading-relaxed">
                Inscris-toi gratuitement et accède aux offres publiées dans ta région.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" className="w-full sm:w-auto bg-background text-foreground hover:bg-background/90">
                  <Link to="/inscription?type=etudiant">Je suis étudiant</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-background/30 bg-transparent text-background hover:bg-background/10 hover:border-background/50"
                >
                  <Link to="/inscription?type=recruteur">Je suis recruteur</Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
